import { useState } from "react";
import { KeyRound, Laptop, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  SkeletonText,
  StatusPill,
} from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useMe, useRevokeAllOtherSessions, useSessions } from "@/lib/queries";
import { TotpRotationModal } from "@/components/auth/TotpRotationModal";
import { formatDateTime, formatRelative } from "@/lib/utils/format";

export function SecurityPage() {
  const meQuery = useMe();
  const sessionsQuery = useSessions();
  const revokeAllOther = useRevokeAllOtherSessions();
  const { toast } = useToast();

  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const [rotationOpen, setRotationOpen] = useState(false);

  async function handleRevokeAllOther() {
    await revokeAllOther.mutateAsync();
    toast({ title: "Se han cerrado las demas sesiones", variant: "success" });
  }

  const sessions = sessionsQuery.data ?? [];
  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div>
      <PageHeader title="Seguridad" description="Sesiones activas, autenticacion en dos pasos y actividad reciente." />

      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Sesiones"
            description="Dispositivos con acceso activo a esta consola."
            action={
              <Button
                variant="outline"
                size="sm"
                disabled={otherSessionsCount === 0}
                onClick={() => setConfirmRevokeOpen(true)}
              >
                Cerrar las demas sesiones
              </Button>
            }
          />
          <CardBody>
            {sessionsQuery.isLoading && <SkeletonText lines={3} />}
            {sessionsQuery.isError && (
              <ErrorState description="No se pudieron cargar las sesiones." onRetry={() => sessionsQuery.refetch()} />
            )}
            {sessions.length > 0 && (
              <ul className="divide-y divide-ink/5">
                {sessions.map((session) => (
                  <li key={session.id} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Laptop className="mt-0.5 h-4.5 w-4.5 shrink-0 text-ink-faint" style={{ height: "1.125rem", width: "1.125rem" }} aria-hidden="true" />
                      <div>
                        <p className="max-w-sm truncate text-sm font-medium text-ink">
                          {session.userAgent ?? "Dispositivo desconocido"}
                        </p>
                        <p className="text-xs text-ink-faint">
                          {session.ipAddress ?? "IP desconocida"} - ultima actividad {formatRelative(session.lastSeenAt)}
                        </p>
                        <p className="text-xs text-ink-faint">Expira: {formatDateTime(session.expiresAt)}</p>
                      </div>
                    </div>
                    {session.isCurrent && <StatusPill tone="brand">Sesion actual</StatusPill>}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Autenticacion en dos pasos" description="Estado del autenticador TOTP para tu cuenta." />
          <CardBody>
            {meQuery.isLoading && <SkeletonText lines={2} />}
            {meQuery.isError && (
              <ErrorState description="No se pudo cargar el estado de seguridad." onRetry={() => meQuery.refetch()} />
            )}
            {meQuery.data && (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-success-600" aria-hidden="true" />
                  <div>
                    <StatusPill tone={meQuery.data.totpEnabled ? "success" : "warning"}>
                      {meQuery.data.totpEnabled ? "TOTP activo" : "TOTP no configurado"}
                    </StatusPill>
                    <p className="mt-1 text-sm text-ink-muted">{meQuery.data.user.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
                  onClick={() => setRotationOpen(true)}
                >
                  Regenerar codigos de recuperacion
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Actividad reciente" description="Consulta el historial completo en Auditoria." />
          <CardBody>
            {meQuery.data && (
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-ink-faint">Sesion iniciada</dt>
                  <dd className="text-ink">{formatDateTime(meQuery.data.session.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-faint">Ultima actividad</dt>
                  <dd className="text-ink">{formatDateTime(meQuery.data.session.lastSeenAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-faint">IP</dt>
                  <dd className="text-ink">{meQuery.data.session.ipAddress ?? "-"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-faint">Expira</dt>
                  <dd className="text-ink">{formatDateTime(meQuery.data.session.expiresAt)}</dd>
                </div>
              </dl>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmRevokeOpen}
        onClose={() => setConfirmRevokeOpen(false)}
        onConfirm={handleRevokeAllOther}
        title="Cerrar las demas sesiones"
        description="Se cerrara el acceso en cualquier otro dispositivo. Tu sesion actual no se vera afectada."
        confirmLabel="Cerrar sesiones"
        variant="danger"
      />

      <TotpRotationModal
        open={rotationOpen}
        onClose={() => setRotationOpen(false)}
        onComplete={() => {
          setRotationOpen(false);
          toast({
            title: "Autenticador actualizado",
            description: "Se han generado nuevos codigos de recuperacion.",
            variant: "success",
          });
        }}
      />
    </div>
  );
}
